/**
 * CADViewerPage - Main page for 3D CAD viewer prototype
 *
 * Layout: Left panel (upload + model list) | Center (3D viewer) | Right panel (metadata + AI analysis)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ViewInAr as CADIcon,
  Compare as CompareIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  CADModelViewer,
  CADUploadZone,
  CADMetadataPanel,
  CADAIAnalysisPanel,
  CADModelList,
  CADChatPanel,
} from '../components/cad';
import { CADMarkerDetailPanel } from '../components/cad/CADMarkerDetailPanel';
import { selectCadEnabled } from '@features/featureFlags/featureFlagsSlice';
import { useAppSelector } from '@store/index';
import { listCADModels, deleteCADModel, getGLBUrl, getCADModel, compareCADModels } from '../services/cad.service';
import { CADMarker, CADModel, CADComparisonSummary } from '../types/cad';
import { useDRBFMWithCAD } from '../hooks/useDRBFMWithCAD';

const CADViewerPage: React.FC = () => {
  const { t } = useTranslation();
  const cadEnabled = useAppSelector(selectCadEnabled);
  const [models, setModels] = useState<CADModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<CADModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState(0);
  const [chatMarkers, setChatMarkers] = useState<CADMarker[]>([]);
  const [selectedMarker, setSelectedMarker] = useState<CADMarker | null>(null);

  // URL params for DRBFM↔CAD linker (TG #673). Open `/cad-viewer?drbfm_session=<id>`
  // and (optionally) `&cad_model=<id>` to overlay failure-mode pins from a DRBFM run.
  const [searchParams] = useSearchParams();
  const drbfmSessionId = searchParams.get('drbfm_session');
  const cadModelParamId = searchParams.get('cad_model');
  const focusFailureModeId = searchParams.get('failure_mode');

  const {
    markers: drbfmMarkers,
    loading: drbfmLoading,
    error: drbfmError,
  } = useDRBFMWithCAD(drbfmSessionId);

  // Only render DRBFM markers when their anchored cad_model_id matches the
  // currently displayed model. Anchors are in the source CAD's coordinate
  // system, so dropping them onto a different mesh would put pins at
  // misleading positions.
  const drbfmMarkersForCurrentModel = useMemo<CADMarker[]>(() => {
    if (!selectedModel) return [];
    return drbfmMarkers.filter((marker) => {
      const payload = marker.detailPayload as { cadAnchor?: { cadModelId?: string } } | undefined;
      const anchorModelId = payload?.cadAnchor?.cadModelId;
      return anchorModelId === selectedModel.id;
    });
  }, [drbfmMarkers, selectedModel]);

  const mergedMarkers = useMemo<CADMarker[]>(
    () => [...chatMarkers, ...drbfmMarkersForCurrentModel],
    [chatMarkers, drbfmMarkersForCurrentModel],
  );

  // When the viewer is opened via the DRBFMTable per-row 3D view button,
  // `?failure_mode=<id>` is present. Auto-open the detail drawer for that
  // anchor as soon as the matching marker shows up. One-shot: manual marker
  // clicks afterwards still drive the drawer normally (TG #673).
  const focusAppliedRef = useRef(false);
  useEffect(() => {
    if (focusAppliedRef.current) return;
    if (!focusFailureModeId) return;
    const match = drbfmMarkersForCurrentModel.find(
      (m) => m.failureModeId === focusFailureModeId,
    );
    if (match) {
      setSelectedMarker(match);
      focusAppliedRef.current = true;
    }
  }, [focusFailureModeId, drbfmMarkersForCurrentModel]);

  // Comparison state (Issue #489 P0)
  const [comparisonModel, setComparisonModel] = useState<CADModel | null>(null);
  const [viewerMode, setViewerMode] = useState<'single' | 'compare'>('single');
  const [comparisonSummary, setComparisonSummary] = useState<CADComparisonSummary | null>(null);
  const [comparing, setComparing] = useState(false);

  // Reset markers on model change
  useEffect(() => { setChatMarkers([]); }, [selectedModel?.id]);

  // Load models on mount (only when feature is confirmed enabled)
  const loadModels = useCallback(async () => {
    try {
      const result = await listCADModels();
      setModels(result.models);
    } catch (err: any) {
      setError(t('cad.loadModelsFailed'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the load-error message; excluded so a language change doesn't refetch the model list (PR #790)
  }, []);

  useEffect(() => {
    if (cadEnabled === true) {
      loadModels();
    }
  }, [cadEnabled, loadModels]);

  // Auto-select the CAD model when arriving via `?cad_model=<id>` (TG #673).
  // One-shot: only the initial resolution runs. Subsequent manual model
  // changes are not overridden even though the URL param still references
  // the original model.
  const cadModelAutoSelectAppliedRef = useRef(false);
  useEffect(() => {
    if (cadModelAutoSelectAppliedRef.current) return;
    if (!cadModelParamId || models.length === 0) return;
    const found = models.find((m) => m.id === cadModelParamId);
    if (found) {
      setSelectedModel(found);
      cadModelAutoSelectAppliedRef.current = true;
    }
  }, [cadModelParamId, models]);

  // Poll processing models until they complete
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const processingIds = models
      .filter((m) => m.status === 'processing' || m.status === 'pending')
      .map((m) => m.id);

    if (processingIds.length === 0) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    if (pollingRef.current) return; // already polling

    pollingRef.current = setInterval(async () => {
      for (const id of processingIds) {
        try {
          const updated = await getCADModel(id);
          setModels((prev) =>
            prev.map((m) => {
              if (m.id !== id) return m;
              if (m.status === updated.status) return m;
              return updated;
            })
          );
          setSelectedModel((prev) => {
            if (prev?.id !== id) return prev;
            if (prev.status === updated.status) return prev;
            return updated;
          });
        } catch {
          // ignore polling errors
        }
      }
    }, 3000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [models]);

  // Handle new upload
  const handleUploadComplete = useCallback(
    (model: CADModel) => {
      setModels((prev) => [model, ...prev]);
      setSelectedModel(model);
    },
    []
  );

  // Handle model selection
  const handleSelect = useCallback((model: CADModel) => {
    setSelectedModel(model);
  }, []);

  // Handle delete
  const handleDeleteClick = useCallback((modelId: string) => {
    setDeleteTargetId(modelId);
    setDeleteDialogOpen(true);
  }, []);

  // Handle setting comparison target
  const handleSetComparisonTarget = useCallback((model: CADModel) => {
    if (model.status !== 'completed') return;
    setComparisonModel(model);
  }, []);

  // Handle starting comparison
  const handleStartComparison = useCallback(async () => {
    if (!selectedModel || !comparisonModel) return;
    setComparing(true);
    try {
      const result = await compareCADModels(selectedModel.id, comparisonModel.id);
      setComparisonSummary(result);
      setViewerMode('compare');
      setRightTab(2); // Switch to chat tab
    } catch (err: any) {
      setError(t('cad.comparisonFailed', { message: err?.message || '' }));
    } finally {
      setComparing(false);
    }
  }, [selectedModel, comparisonModel, t]);

  // Handle exiting comparison mode
  const handleExitComparison = useCallback(() => {
    setViewerMode('single');
    setComparisonModel(null);
    setComparisonSummary(null);
    setChatMarkers([]);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTargetId) return;

    try {
      await deleteCADModel(deleteTargetId);
      setModels((prev) => prev.filter((m) => m.id !== deleteTargetId));
      if (selectedModel?.id === deleteTargetId) {
        setSelectedModel(null);
      }
      // Clear comparison state if deleted model is the comparison target
      if (comparisonModel?.id === deleteTargetId) {
        handleExitComparison();
      }
    } catch (err: any) {
      setError(t('cad.deleteFailed'));
    }
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  }, [deleteTargetId, selectedModel, comparisonModel, handleExitComparison, t]);

  const glbUrl = selectedModel?.status === 'completed' ? getGLBUrl(selectedModel.id) : null;
  const comparisonGlbUrl = comparisonModel?.status === 'completed' ? getGLBUrl(comparisonModel.id) : null;

  if (cadEnabled === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (cadEnabled === false) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="info" sx={{ maxWidth: 500, mx: 'auto' }}>
          {t('cad.disabled')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CADIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          {t('cad.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {t('cad.prototype')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* DRBFM session linkage banner (TG #673) */}
      {drbfmSessionId && drbfmLoading && (
        <Alert severity="info">{t('cad.drbfmLoading')}</Alert>
      )}
      {drbfmSessionId && drbfmError && (
        <Alert severity="warning">
          {t('cad.drbfmLoadFailed', { message: drbfmError })}
        </Alert>
      )}
      {drbfmSessionId && !drbfmLoading && !drbfmError && drbfmMarkers.length > 0 && (
        <Alert severity={drbfmMarkersForCurrentModel.length > 0 ? 'success' : 'warning'}>
          {drbfmMarkersForCurrentModel.length > 0 ? (
            t('cad.drbfmLinked', {
              shown: drbfmMarkersForCurrentModel.length,
              total: drbfmMarkers.length,
            })
          ) : (
            t('cad.drbfmNoMatchingModel', { total: drbfmMarkers.length })
          )}
        </Alert>
      )}

      {/* Main content: 3-column layout */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0 }}>
        {/* Left panel: Upload + Model list */}
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <Box sx={{ p: 1.5 }}>
            <CADUploadZone onUploadComplete={handleUploadComplete} />
          </Box>
          <Divider />
          <Box sx={{ p: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('cad.uploadedModels', { count: models.length })}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
            <CADModelList
              models={models}
              selectedModelId={selectedModel?.id || null}
              onSelect={handleSelect}
              onDelete={handleDeleteClick}
              onSetComparisonTarget={handleSetComparisonTarget}
              comparisonModelId={comparisonModel?.id || null}
            />
          </Box>
          {/* Comparison controls */}
          {selectedModel && comparisonModel && viewerMode === 'single' && (
            <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                {t('cad.comparisonTarget')}
              </Typography>
              <Chip
                label={comparisonModel.filename}
                size="small"
                onDelete={() => setComparisonModel(null)}
                sx={{ mb: 1, maxWidth: '100%' }}
              />
              <Button
                variant="contained"
                size="small"
                fullWidth
                startIcon={comparing ? <CircularProgress size={16} /> : <CompareIcon />}
                onClick={handleStartComparison}
                disabled={comparing}
              >
                {comparing ? t('cad.comparing') : t('cad.startComparison')}
              </Button>
            </Box>
          )}
        </Paper>

        {/* Center: 3D Viewer(s) */}
        {viewerMode === 'single' ? (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
          >
            <CADModelViewer
              glbUrl={glbUrl}
              filename={selectedModel?.filename}
              markers={mergedMarkers}
              onMarkerClick={(marker) => {
                // Only DRBFM markers carry a back-pointer to a FailureMode and a
                // payload to render in the detail drawer. Chat markers have neither
                // — clicking them would otherwise pop an empty drawer.
                if (!marker.failureModeId || !marker.detailPayload) return;
                setSelectedMarker(marker);
              }}
            />
          </Paper>
        ) : (
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Comparison header */}
            <Paper elevation={0} variant="outlined" sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CompareIcon fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={600}>{t('cad.comparisonMode')}</Typography>
              <Chip
                label={t('cad.confidenceLabel', {
                  value: comparisonSummary?.confidence?.toUpperCase() || '-',
                })}
                size="small"
                color={comparisonSummary?.confidence === 'high' ? 'success' : comparisonSummary?.confidence === 'medium' ? 'warning' : 'error'}
                variant="outlined"
              />
              <Box sx={{ flex: 1 }} />
              <Tooltip title={t('cad.exitComparisonMode')}>
                <IconButton size="small" onClick={handleExitComparison}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
            {/* Side-by-side viewers */}
            <Box sx={{ flex: 1, display: 'flex', gap: 1, minHeight: 0 }}>
              <Paper elevation={0} variant="outlined" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', position: 'relative' }}>
                <Typography variant="caption" sx={{ position: 'absolute', top: 4, left: 8, zIndex: 1, bgcolor: 'background.paper', px: 0.5, borderRadius: 1, fontWeight: 600 }}>
                  A: {selectedModel?.filename}
                </Typography>
                <CADModelViewer glbUrl={glbUrl} filename={selectedModel?.filename} markers={chatMarkers.filter(m => !m.side || m.side === 'A')} />
              </Paper>
              <Paper elevation={0} variant="outlined" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', position: 'relative' }}>
                <Typography variant="caption" sx={{ position: 'absolute', top: 4, left: 8, zIndex: 1, bgcolor: 'background.paper', px: 0.5, borderRadius: 1, fontWeight: 600 }}>
                  B: {comparisonModel?.filename}
                </Typography>
                <CADModelViewer glbUrl={comparisonGlbUrl} filename={comparisonModel?.filename} markers={chatMarkers.filter(m => m.side === 'B')} />
              </Paper>
            </Box>
          </Box>
        )}

        {/* Right panel: Tabs (Metadata / AI Analysis / Chat) */}
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <Tabs
            value={rightTab}
            onChange={(_, v) => setRightTab(v)}
            variant="fullWidth"
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, fontSize: '0.75rem' } }}
          >
            <Tab label={t('cad.metadataTab')} />
            <Tab label={t('cad.aiAnalysisTab')} />
            <Tab label={t('cad.chatTab')} />
          </Tabs>
          <Divider />
          <Box sx={{ flex: 1, overflow: 'auto', display: rightTab === 0 ? 'block' : 'none' }}>
            <Box sx={{ p: 1.5 }}>
              <CADMetadataPanel
                metadata={selectedModel?.metadata || null}
                filename={selectedModel?.filename}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', display: rightTab === 1 ? 'block' : 'none' }}>
            <Box sx={{ p: 1.5 }}>
              <CADAIAnalysisPanel
                modelId={selectedModel?.id || null}
                existingAnalysis={selectedModel?.aiAnalysis || null}
                onAnalysisComplete={(analysis) => {
                  if (selectedModel) {
                    setSelectedModel({ ...selectedModel, aiAnalysis: analysis });
                    setModels((prev) =>
                      prev.map((m) =>
                        m.id === selectedModel.id ? { ...m, aiAnalysis: analysis } : m
                      )
                    );
                  }
                }}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden', display: rightTab === 2 ? 'flex' : 'none', flexDirection: 'column' }}>
            <CADChatPanel
              modelId={selectedModel?.id || null}
              modelName={selectedModel?.filename}
              onMarkersUpdate={setChatMarkers}
              comparisonModelId={viewerMode === 'compare' ? comparisonModel?.id : undefined}
              comparisonModelName={viewerMode === 'compare' ? comparisonModel?.filename : undefined}
            />
          </Box>
        </Paper>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('cad.deleteDialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('cad.deleteDialogBody')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DRBFM marker detail drawer (TG #673) */}
      <CADMarkerDetailPanel
        open={Boolean(selectedMarker)}
        marker={selectedMarker}
        onClose={() => setSelectedMarker(null)}
      />
    </Box>
  );
};

export default CADViewerPage;
